import React from 'react';
import { Check, X } from 'lucide-react';
import { checkPasswordStrength } from '../utils/passwordStrength';

/**
 * Compact inline password strength meter + checklist.
 * Pass `password` for live feedback. Hidden when password is empty.
 */
export const PasswordStrengthHint = ({ password = '' }) => {
  if (!password) return null;
  const { checks, label, color, passedCount } = checkPasswordStrength(password);
  const segments = 4;

  const Item = ({ ok, children, testId }) => (
    <li className="flex items-center gap-2" data-testid={testId}>
      {ok ? (
        <Check className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
      ) : (
        <X className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
      )}
      <span className={ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}>
        {children}
      </span>
    </li>
  );

  return (
    <div className="space-y-2 text-xs" data-testid="password-strength-hint">
      <div className="flex items-center gap-2">
        <div className="flex-1 flex gap-1">
          {Array.from({ length: segments }).map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${i < passedCount ? color : 'bg-muted'}`}
            />
          ))}
        </div>
        <span className="text-xs font-medium text-muted-foreground min-w-[3rem] text-right" data-testid="password-strength-label">
          {label}
        </span>
      </div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-1">
        <Item ok={checks.length} testId="pw-rule-length">At least 8 characters</Item>
        <Item ok={checks.letter} testId="pw-rule-letter">A letter (a–z)</Item>
        <Item ok={checks.number} testId="pw-rule-number">A number (0–9)</Item>
        <Item ok={checks.symbol} testId="pw-rule-symbol">A symbol (!@#…)</Item>
      </ul>
    </div>
  );
};

export default PasswordStrengthHint;
